import { useState } from "react";
import { Alert, Badge, Button, Group, Stack, Text, TextInput, UnstyledButton } from "@mantine/core";
import { Info, Search } from "lucide-react";
import type { PeppolLookup } from "@/lib/api/client";
import { einvoiceHelp, peppolStatusText } from "@/lib/einvoice";

/**
 * The e-invoice address input of the billing-details form: live help for what was typed (GLN / PEPPOL id
 * — the same rules the backend applies), the backend's own message inline on a refused save, a
 * "Find our Peppol ID" lookup by organisation number, and the soft Peppol Directory confirmation of the
 * saved id. Nothing here blocks saving except what the backend refuses.
 */
export function EinvoiceAddressField({
  value,
  onChange,
  organisationNumber,
  serverError,
  savedAddress,
  peppolRegistered,
  manualSetup,
  lookup,
}: {
  value: string;
  onChange: (value: string) => void;
  organisationNumber: string | null;
  /** The backend's 400 message from the last save, cleared by the parent on edit. */
  serverError: string | null;
  /** The stored address (the soft confirmation and the manual-setup note describe it, not the draft). */
  savedAddress: string;
  peppolRegistered: boolean | null;
  manualSetup: boolean;
  /** Peppol Directory lookup by the organisation number on record; omitted when there is none yet. */
  lookup?: () => Promise<PeppolLookup>;
}) {
  const [result, setResult] = useState<PeppolLookup | null>(null);
  const [searching, setSearching] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);
  const help = einvoiceHelp(value, organisationNumber);
  const unchanged = value.replace(/\s/g, "") === savedAddress.replace(/\s/g, "") && savedAddress !== "";
  const status = unchanged ? peppolStatusText(peppolRegistered) : null;

  const find = async () => {
    if (!lookup) return;
    setSearching(true);
    setLookupFailed(false);
    try {
      setResult(await lookup());
    } catch {
      setResult(null);
      setLookupFailed(true);
    } finally {
      setSearching(false);
    }
  };

  return (
    <Stack gap={6}>
      <TextInput
        label="E-invoice address (GLN or PEPPOL id)"
        required
        placeholder="0007:5560360793"
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        description={help.hint}
        error={serverError || (value.trim() ? help.problem : null) || undefined}
      />
      {status && <Text fz="xs" c={status.color === "green" ? "green.7" : "dimmed"}>{status.text}</Text>}
      {unchanged && manualSetup && (
        <Alert color="blue" icon={<Info size={16} />} p="sm">
          <Text fz="sm">Saved. E-invoicing to this id is set up by hand in the accounting system.</Text>
        </Alert>
      )}
      {lookup && (
        <Group gap="xs">
          <Button variant="subtle" size="compact-sm" leftSection={<Search size={14} />} loading={searching} onClick={find}>
            Find our Peppol ID
          </Button>
        </Group>
      )}
      {lookupFailed && <Text fz="xs" c="dimmed">The lookup didn't work right now — try again in a minute.</Text>}
      {result?.skipped && <Text fz="xs" c="dimmed">The Peppol Directory didn't answer — try again in a minute.</Text>}
      {result && !result.skipped && result.matches.length === 0 && (
        <Text fz="xs" c="dimmed">
          No Peppol registration found for your organisation number. Listing is optional — ask your e-invoice
          provider for your id.
        </Text>
      )}
      {result && result.matches.length > 0 && (
        <Stack gap={4}>
          <Text fz="xs" c="dimmed">Pick one to use it:</Text>
          {result.matches.map((m) => (
            <UnstyledButton
              key={m.id}
              onClick={() => onChange(m.id)}
              p={6}
              style={{ borderRadius: 6, border: "1px solid var(--mantine-color-default-border)" }}
            >
              <Group gap="xs" wrap="wrap">
                <Text fz="sm" ff="monospace">{m.id}</Text>
                <Text fz="sm" c="dimmed">{m.name}</Text>
                {m.can_receive_invoice && <Badge size="xs" color="green" variant="light">Can receive invoices</Badge>}
              </Group>
            </UnstyledButton>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
